import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface SchoolProfileData {
  id: string
  name: string
  legalBusinessName: string | null
  businessStructure: 'sole_proprietorship' | 'partnership' | 'corporation' | 'other' | null
  businessRegistrationNumber: string | null
  businessEmail: string | null
  businessPhone: string | null
  businessAddress: string | null
  businessAddressPlaceId: string | null
  isMtoLicensed: boolean | null
  mtoLicenceNumber: string | null
  mtoLicenceExpiryDate: string | null
  mtoLicenceVerificationStatus: 'unverified' | 'pending_review' | 'verified' | 'rejected'
  websiteUrl: string | null
  logoPath: string | null
  profileCompletedAt: string | null
}

export interface SchoolDocument {
  id: string
  documentType: 'business_registration' | 'mto_licence' | 'other'
  title: string
  status: 'pending_upload' | 'active'
  createdAt: string
}

export interface SaveFields {
  legalBusinessName: string
  tradingName: string
  businessStructure: 'sole_proprietorship' | 'partnership' | 'corporation' | 'other' | ''
  businessRegistrationNumber: string
  businessEmail: string
  businessPhone: string
  businessAddress: string
  businessAddressPlaceId: string | null
  isMtoLicensed: boolean | null
  mtoLicenceNumber: string
  mtoLicenceExpiryDate: string
  websiteUrl: string
}

interface SaveResult {
  ok: boolean
  code?: string
  complete?: boolean
  missing_fields?: string[]
}

const LOGO_BUCKET = 'school-logos'

/**
 * Fetch + save for a school's business profile (migration 080) — the single shared data
 * layer for both the required onboarding wizard (OnboardingWizard.tsx) and the Settings
 * "School profile" edit tab, so there is exactly one place this logic lives. save() calls
 * update_school_profile(), the one RPC both surfaces write through; it always succeeds
 * once authorized and reports completeness via complete/missing_fields rather than a hard
 * rejection, so the wizard can save progress between steps.
 */
export function useSchoolProfile(schoolId: string | null) {
  const [school, setSchool] = useState<SchoolProfileData | null>(null)
  const [documents, setDocuments] = useState<SchoolDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingDocument, setUploadingDocument] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!schoolId) { setSchool(null); setDocuments([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const [{ data: row, error: schoolErr }, { data: docs }] = await Promise.all([
        supabase
          .from('schools')
          .select('id, name, legal_business_name, business_structure, business_registration_number, business_email, phone, address, business_address_place_id, is_mto_licensed, mto_licence_number, mto_licence_expiry_date, mto_licence_verification_status, website_url, logo_path, profile_completed_at')
          .eq('id', schoolId)
          .maybeSingle(),
        supabase
          .from('school_documents')
          .select('id, document_type, title, status, created_at')
          .eq('school_id', schoolId)
          .eq('status', 'active')
          .order('created_at', { ascending: false }),
      ])

      if (schoolErr || !row) { setError('Could not load this school.'); return }

      setSchool({
        id: row.id,
        name: row.name,
        legalBusinessName: row.legal_business_name,
        businessStructure: row.business_structure,
        businessRegistrationNumber: row.business_registration_number,
        businessEmail: row.business_email,
        businessPhone: row.phone,
        businessAddress: row.address,
        businessAddressPlaceId: row.business_address_place_id,
        isMtoLicensed: row.is_mto_licensed,
        mtoLicenceNumber: row.mto_licence_number,
        mtoLicenceExpiryDate: row.mto_licence_expiry_date,
        mtoLicenceVerificationStatus: row.mto_licence_verification_status,
        websiteUrl: row.website_url,
        logoPath: row.logo_path,
        profileCompletedAt: row.profile_completed_at,
      })
      setDocuments((docs ?? []).map(d => ({
        id: d.id,
        documentType: d.document_type,
        title: d.title,
        status: d.status,
        createdAt: d.created_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [schoolId])

  useEffect(() => { fetchAll() }, [fetchAll])

  const save = useCallback(async (fields: SaveFields): Promise<SaveResult> => {
    if (!schoolId || saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      // update_school_profile()'s own validation treats an empty string exactly like
      // null (trim(...) = ''), so empty string is used here for the two required,
      // no-default params (p_legal_business_name/p_trading_name) rather than null —
      // the generated RPC arg type for a non-defaulted SQL param is plain `string`, not
      // `string | null`. Every defaulted param below uses `undefined` (its generated
      // type is `T | undefined`, never `T | null`) to mean "leave unset."
      const { data, error: rpcError } = await supabase.rpc('update_school_profile', {
        p_school_id: schoolId,
        p_legal_business_name: fields.legalBusinessName.trim(),
        p_trading_name: fields.tradingName.trim(),
        // p_business_structure has no SQL default (unlike the fields below), so its
        // generated arg type has no `| undefined`/`| null` even though the RPC body
        // itself handles a genuinely null value fine (same "business_structure" missing
        // check as every other required field) — a codegen strictness quirk, not a real
        // constraint; `as any` only bypasses the overly strict generated type here.
        p_business_structure: (fields.businessStructure || null) as any,
        p_business_registration_number: fields.businessRegistrationNumber.trim() || undefined,
        p_business_email: fields.businessEmail.trim() || undefined,
        p_business_phone: fields.businessPhone.trim() || undefined,
        p_business_address: fields.businessAddress.trim() || undefined,
        p_business_address_place_id: fields.businessAddressPlaceId ?? undefined,
        p_is_mto_licensed: fields.isMtoLicensed ?? undefined,
        p_mto_licence_number: fields.mtoLicenceNumber.trim() || undefined,
        p_mto_licence_expiry_date: fields.mtoLicenceExpiryDate || undefined,
        p_website_url: fields.websiteUrl.trim() || undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as unknown as SaveResult
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setSaving(false)
    }
  }, [schoolId, saving, fetchAll])

  const uploadLogo = useCallback(async (file: File): Promise<{ ok: boolean; code?: string }> => {
    if (!schoolId || uploadingLogo) return { ok: false, code: 'busy' }
    setUploadingLogo(true)
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase()
      const path = `${schoolId}/${crypto.randomUUID()}.${ext}`
      const { error: upErr } = await supabase.storage.from(LOGO_BUCKET).upload(path, file, { contentType: file.type, upsert: false })
      if (upErr) return { ok: false, code: 'upload_failed' }

      const { data, error: rpcError } = await supabase.rpc('set_school_logo', { p_school_id: schoolId, p_logo_path: path })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string }
      if (result?.ok) await fetchAll()
      return result ?? { ok: false, code: 'internal_error' }
    } finally {
      setUploadingLogo(false)
    }
  }, [schoolId, uploadingLogo, fetchAll])

  const logoUrl = useCallback((path: string | null) => {
    if (!path) return null
    return supabase.storage.from(LOGO_BUCKET).getPublicUrl(path).data.publicUrl
  }, [])

  const uploadDocument = useCallback(async (
    file: File, documentType: 'business_registration' | 'mto_licence' | 'other', title: string,
  ): Promise<{ ok: boolean; code?: string }> => {
    if (!schoolId || uploadingDocument) return { ok: false, code: 'busy' }
    setUploadingDocument(true)
    try {
      const { data: draft, error: draftError } = await supabase.rpc('create_school_document_draft', {
        p_school_id: schoolId,
        p_document_type: documentType,
        p_title: title,
      })
      if (draftError || !draft) return { ok: false, code: 'internal_error' }
      const { document_id, storage_path } = (Array.isArray(draft) ? draft[0] : draft) as { document_id: string; storage_path: string }

      const { error: upErr } = await supabase.storage.from('school-docs').upload(storage_path, file, { contentType: file.type, upsert: false })
      if (upErr) return { ok: false, code: 'upload_failed' }

      const { error: finalizeError } = await supabase.rpc('finalize_school_document', { p_document_id: document_id })
      if (finalizeError) return { ok: false, code: 'finalize_failed' }

      await fetchAll()
      return { ok: true }
    } finally {
      setUploadingDocument(false)
    }
  }, [schoolId, uploadingDocument, fetchAll])

  const documentUrl = useCallback(async (documentId: string): Promise<string | null> => {
    const { data, error: fnError } = await supabase.functions.invoke('get-school-document-url', { body: { documentId } })
    if (fnError || !data?.url) return null
    return data.url as string
  }, [])

  return {
    school, documents, loading, error, saving, uploadingLogo, uploadingDocument,
    save, uploadLogo, logoUrl, uploadDocument, documentUrl, refresh: fetchAll,
  }
}
